/**
 * Project Templates
 * Pre-configured project layouts for quick start
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import {
  X,
  Music,
  Guitar,
  Drum,
  Mic,
  Radio,
  Waves,
  Headphones,
  Sparkles,
  Volume2,
  Zap,
  Crown,
  ChevronRight,
  FolderPlus,
} from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';

// Flame icon component (not in lucide by default) - defined before PROJECT_TEMPLATES
const Flame = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
);

// Project template definitions
export const PROJECT_TEMPLATES = [
  {
    id: 'empty',
    name: 'Empty Project',
    description: 'Start with a blank canvas',
    icon: FolderPlus,
    color: 'from-zinc-600 to-zinc-700',
    tier: 'free',
    tracks: [
      { name: 'Track 1', type: 'audio' },
    ],
  },
  {
    id: 'rock-band',
    name: 'Rock Band Session',
    description: 'Drums, bass, and 2 guitar tracks',
    icon: Guitar,
    color: 'from-red-500 to-orange-500',
    tier: 'free',
    tracks: [
      { name: 'Drums', type: 'audio', guitarEffects: null },
      { name: 'Bass', type: 'audio', guitarEffects: { enabled: true, ampEnabled: true, ampModel: 'crunch' } },
      { name: 'Lead Guitar', type: 'audio', guitarEffects: { enabled: true, ampEnabled: true, ampModel: 'highGain' } },
      { name: 'Rhythm Guitar', type: 'audio', guitarEffects: { enabled: true, ampEnabled: true, ampModel: 'crunch' } },
    ],
  },
  {
    id: 'acoustic',
    name: 'Acoustic Session',
    description: 'Acoustic guitar with vocals',
    icon: Music,
    color: 'from-amber-500 to-yellow-500',
    tier: 'free',
    tracks: [
      { name: 'Acoustic Guitar', type: 'audio', guitarEffects: { enabled: true, ampEnabled: true, ampModel: 'clean' } },
      { name: 'Vocals', type: 'audio' },
      { name: 'Backing Vocals', type: 'audio' },
    ],
  },
  {
    id: 'electronic',
    name: 'Electronic Beat',
    description: 'Synths, drums, and samples',
    icon: Zap,
    color: 'from-cyan-500 to-blue-500',
    tier: 'pro',
    tracks: [
      { name: 'Drums', type: 'instrument', instrumentType: 'drums' },
      { name: 'Bass Synth', type: 'instrument', instrumentType: 'synth' },
      { name: 'Lead Synth', type: 'instrument', instrumentType: 'synth' },
      { name: 'Pads', type: 'instrument', instrumentType: 'synth' },
      { name: 'FX & Samples', type: 'audio' },
    ],
  },
  {
    id: 'podcast',
    name: 'Podcast / Voiceover',
    description: 'Voice tracks with intro/outro',
    icon: Mic,
    color: 'from-violet-500 to-purple-500',
    tier: 'free',
    tracks: [
      { name: 'Host', type: 'audio' },
      { name: 'Guest', type: 'audio' },
      { name: 'Intro Music', type: 'audio' },
      { name: 'Outro Music', type: 'audio' },
    ],
  },
  {
    id: 'metal',
    name: 'Metal Production',
    description: 'High-gain guitars with double tracking',
    icon: Flame,
    color: 'from-red-600 to-red-800',
    tier: 'pro',
    tracks: [
      { name: 'Drums', type: 'audio' },
      { name: 'Bass', type: 'audio', guitarEffects: { enabled: true, ampEnabled: true, ampModel: 'metal' } },
      { name: 'Rhythm L', type: 'audio', guitarEffects: { enabled: true, ampEnabled: true, ampModel: 'metal' }, pan: -0.8 },
      { name: 'Rhythm R', type: 'audio', guitarEffects: { enabled: true, ampEnabled: true, ampModel: 'metal' }, pan: 0.8 },
      { name: 'Lead', type: 'audio', guitarEffects: { enabled: true, ampEnabled: true, ampModel: 'highGain' } },
      { name: 'Vocals', type: 'audio' },
    ],
  },
  {
    id: 'lo-fi',
    name: 'Lo-Fi Beats',
    description: 'Chill beats with vinyl warmth',
    icon: Headphones,
    color: 'from-pink-500 to-rose-500',
    tier: 'pro',
    tracks: [
      { name: 'Drums', type: 'instrument', instrumentType: 'drums' },
      { name: 'Keys', type: 'instrument', instrumentType: 'synth' },
      { name: 'Bass', type: 'instrument', instrumentType: 'synth' },
      { name: 'Samples', type: 'audio' },
      { name: 'FX', type: 'audio' },
    ],
  },
  {
    id: 'film-score',
    name: 'Film Score',
    description: 'Orchestral layers with ambience',
    icon: Sparkles,
    color: 'from-indigo-500 to-blue-600',
    tier: 'studio',
    tracks: [
      { name: 'Strings', type: 'instrument', instrumentType: 'synth' },
      { name: 'Brass', type: 'instrument', instrumentType: 'synth' },
      { name: 'Woodwinds', type: 'instrument', instrumentType: 'synth' },
      { name: 'Percussion', type: 'instrument', instrumentType: 'drums' },
      { name: 'Choir', type: 'audio' },
      { name: 'Ambience', type: 'audio' },
      { name: 'SFX', type: 'audio' },
      { name: 'Dialog', type: 'audio' },
    ],
  },
];

// Template Card Component
const TemplateCard = ({ template, isLocked, onSelect }) => {
  const Icon = template.icon;
  const navigate = useNavigate();
  
  return (
    <motion.button
      onClick={() => isLocked ? navigate('/pricing') : onSelect(template)}
      className={`relative group w-full text-left p-4 rounded-xl border transition-all ${
        isLocked
          ? 'bg-zinc-900/50 border-zinc-800 opacity-60 hover:border-amber-500/30'
          : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600 hover:bg-zinc-800/50'
      }`}
      whileHover={{ scale: isLocked ? 1 : 1.02 }}
      whileTap={{ scale: isLocked ? 1 : 0.98 }}
      data-testid={`template-${template.id}`}
    >
      {/* Locked Badge */}
      {isLocked && (
        <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 rounded-full">
          <Crown className="w-3 h-3 text-amber-500" />
          <span className="text-[10px] text-amber-400 font-medium uppercase">
            {template.tier}
          </span>
        </div>
      )}
      
      {/* Icon */}
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center mb-3 ${isLocked ? 'opacity-50' : ''}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      
      {/* Content */}
      <h3 className={`font-semibold mb-1 ${isLocked ? 'text-zinc-500' : 'text-white'}`}>
        {template.name}
      </h3>
      <p className="text-xs text-zinc-500 mb-2">{template.description}</p>
      
      {/* Track Count */}
      <div className="flex items-center gap-2 text-[10px] text-zinc-500">
        <Volume2 className="w-3 h-3" />
        <span>{template.tracks.length} tracks</span>
      </div>
      
      {/* Hover Arrow */}
      {!isLocked && (
        <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </motion.button>
  );
};

// Template Selector Modal
export const ProjectTemplateSelector = ({ isOpen, onClose, onSelectTemplate }) => {
  const { isPro, isStudio, planId } = useSubscription();
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Check if template is available for user's tier
  const isTemplateAvailable = (template) => {
    if (template.tier === 'free') return true;
    if (template.tier === 'pro' && (isPro || isStudio)) return true;
    if (template.tier === 'studio' && isStudio) return true;
    return false;
  };
  
  // Filter templates by category
  const filteredTemplates = PROJECT_TEMPLATES.filter(t => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'available') return isTemplateAvailable(t);
    return t.tier === selectedCategory;
  });
  
  const categories = [
    { id: 'all', name: 'All Templates' },
    { id: 'available', name: 'Available' },
    { id: 'free', name: 'Free' },
    { id: 'pro', name: 'Pro' },
    { id: 'studio', name: 'Studio' },
  ];
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[800px] md:max-h-[80vh] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden"
            data-testid="template-selector-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700">
              <div>
                <h2 className="text-xl font-bold text-white">Choose a Template</h2>
                <p className="text-sm text-zinc-400">Start with a pre-configured project layout</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Category Tabs */}
            <div className="px-6 py-3 border-b border-zinc-800 flex gap-2 overflow-x-auto">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-indigo-500 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:text-white'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            
            {/* Templates Grid */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredTemplates.map(template => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    isLocked={!isTemplateAvailable(template)}
                    onSelect={onSelectTemplate}
                  />
                ))}
              </div>
              
              {filteredTemplates.length === 0 && (
                <div className="text-center py-12 text-zinc-500">
                  No templates found in this category
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="px-6 py-3 border-t border-zinc-700 bg-zinc-800/50 flex items-center justify-between">
              <p className="text-xs text-zinc-500">
                {planId === 'free' && 'Upgrade to Pro for more templates'}
                {planId === 'pro' && 'Upgrade to Studio for all templates'}
                {planId === 'studio' && 'You have access to all templates'}
              </p>
              <Button
                variant="ghost"
                onClick={() => onSelectTemplate(PROJECT_TEMPLATES[0])}
                className="text-zinc-400 hover:text-white text-sm"
              >
                Skip - Start Empty
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Hook to create tracks from template
export const useProjectTemplate = () => {
  const createTracksFromTemplate = (template, createTrackFn) => {
    const tracks = template.tracks.map((trackConfig, index) => ({
      id: index,
      name: trackConfig.name,
      type: trackConfig.type || 'audio',
      instrumentType: trackConfig.instrumentType || null,
      audioBuffer: null,
      waveformData: [],
      volume: 0.8,
      pan: trackConfig.pan || 0,
      muted: false,
      solo: false,
      eq: { bass: 0, mid: 0, treble: 0 },
      effects: { reverb: 0, delay: 0 },
      guitarEffects: trackConfig.guitarEffects || null,
      startTime: 0,
      duration: 0,
    }));
    
    return tracks;
  };
  
  return { createTracksFromTemplate };
};

export default ProjectTemplateSelector;
